import { useTypingAnimation } from "@/hooks/useTypingAnimation.tsx";
import { cn } from "@/lib/utils.ts";
import { type ReactNode } from "react";

type TypingTextProps = {
  text?: string;
  children?: ReactNode;
  speed?: number;
  delay?: number;
  isActive?: boolean;
  className?: string;
  onComplete?: () => void;
  // For rich text with formatting
  richText?: Array<string | { text: string; className?: string }>;
};

/**
 * Component that displays text with a typing animation effect
 * Creates a chat-like streaming effect where text appears character by character
 *
 * Supports multiple formats:
 * 1. Plain text with typing animation
 * 2. JSX children (no animation)
 * 3. Rich text array with formatting (typing animation with formatting)
 */
export const TypingText = ({
  text,
  children,
  speed,
  delay = 0,
  isActive = true,
  className,
  onComplete,
  richText,
}: TypingTextProps) => {
  // If children are provided, use them directly (no typing animation for JSX)
  if (children) {
    return <span className={cn("inline-block", className)}>{children}</span>;
  }

  // If richText is provided, use it for formatted typing animation
  if (richText) {
    const { displayedText, isComplete } = useTypingAnimation(
      richText.map((item) => (typeof item === "string" ? item : item.text)).join(""),
      speed,
      delay,
      isActive,
      onComplete,
    );

    // Render the rich text with formatting
    let currentIndex = 0;
    const renderedElements: ReactNode[] = [];

    richText.forEach((item, itemIndex) => {
      const itemText = typeof item === "string" ? item : item.text;
      const itemClassName = typeof item === "string" ? undefined : item.className;

      if (currentIndex + itemText.length <= displayedText.length) {
        // This item is fully displayed
        renderedElements.push(
          <span key={itemIndex} className={itemClassName}>
            {itemText}
          </span>,
        );
        currentIndex += itemText.length;
      } else if (currentIndex < displayedText.length) {
        // This item is partially displayed
        const partialText = displayedText.slice(currentIndex, currentIndex + itemText.length);
        renderedElements.push(
          <span key={itemIndex} className={itemClassName}>
            {partialText}
          </span>,
        );
        currentIndex = displayedText.length;
      }
    });

    return (
      <span className={cn("inline-block", className)}>
        {renderedElements}
        {isActive && !isComplete && <span className="animate-pulse">|</span>}
      </span>
    );
  }

  // Otherwise, use the typing animation for plain text
  const { displayedText, isComplete } = useTypingAnimation(
    text || "",
    speed,
    delay,
    isActive,
    onComplete,
  );

  return (
    <span className={cn("inline-block", className)}>
      {displayedText}
      {isActive && !isComplete && <span className="animate-pulse">|</span>}
    </span>
  );
};
